import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import SEO from 'react-seo-component';

import './category-product-list-style.scss';

import useScroll from '@src/hooks/useScroll';
import { useSnackbar } from '@src/providers/SnackbarProvider';
import { DeviceContext, deviceType, MenuDataContext } from '@providers';

import TopButton from '@src/componentsNew/Buttons/TopButton/TopButton';
import DesktopProductListPageSkeleton from '@src/componentsNew/Skeleton/desktop/page/DesktopProductListPageSkeleton';
import DesktopCategoryProductListTemplate from '@src/componentsNew/ProductListTemplate/Category/DesktopCategoryProductListTemplate';

import MobileProductListPageSkeleton from '@src/componentsNew/Skeleton/mobile/page/MobileProductListPageSkeleton';
import MobileCategoryProductListTemplate from '@src/componentsNew/ProductListTemplate/Category/MobileCategoryProductListTemplate';

import { CATEGORY_FOR_SEO } from '@components/ProductPage/CategoryPage/queries';
import { categoryForSEO } from '@components/ProductPage/CategoryPage/__generated__/categoryForSEO';

import { productsUrl, baseUrl } from '@src/app/routes';
import { getGraphqlIdFromDBId } from '@src/core/utils';
import { DESKTOP_PRODUCT_LIST_REQUEST_COUNT } from '@src/utils/constants';

export const CategoryPage = ({ match }) => {
  const history = useHistory();
  const { isMobile } = useContext<deviceType>(DeviceContext);
  const { showSnackbar } = useSnackbar();
  const { categoryItems } = useContext(MenuDataContext);

  const { isScrolled, scrollTop } = useScroll();
  const currentUrlCategoryGraphqlId = getGraphqlIdFromDBId(match.params.id, 'Category');

  const [isLevelZero, setIsLevelZero] = useState(false);
  const [isNotExistLocalData, setIsNotExistLocalData] = useState(false); // category-navbar에 저장되어 있지 않는 category의 경우 판별
  const [selectedCategoryDBId, setSelectedCategoryDBId] = useState<number | null>(null);

  const [level0Category, setLevel0Category] = useState(null);
  const [level1Category, setLevel1Category] = useState(null);
  const [level2Category, setLevel2Category] = useState(null);

  const { data, loading } = useQuery<categoryForSEO>(CATEGORY_FOR_SEO, {
    variables: {
      id: currentUrlCategoryGraphqlId,
    },
  });

  const setCurrentCategoryInfoFromLocalData = () => {
    const localStorageCategories = localStorage.getItem('category-navbar');

    if (!localStorageCategories) {
      setIsNotExistLocalData(true);
      return;
    }

    const storedCategories = JSON.parse(localStorageCategories);
    const currentUrlCategory = storedCategories.find((category) => category.id === parseInt(match.params.id));

    if (!currentUrlCategory) {
      setIsNotExistLocalData(true);
      return;
    }
    const isCurrentUrlCategoryLevelZero = currentUrlCategory?.level === 0;

    if (isCurrentUrlCategoryLevelZero) {
      const level1FirstCategory = currentUrlCategory?.level1[0];

      setSelectedCategoryDBId(level1FirstCategory?.id);
      setLevel2Category(level1FirstCategory?.child_items);
    } else {
      setSelectedCategoryDBId(parseInt(match.params.id));
      setLevel2Category(currentUrlCategory?.level2);
    }

    setIsLevelZero(isCurrentUrlCategoryLevelZero);
    setIsNotExistLocalData(false);
    setLevel0Category(currentUrlCategory?.level0);
    setLevel1Category(currentUrlCategory?.level1);
  };

  useEffect(() => {
    setCurrentCategoryInfoFromLocalData();
  }, [match.params.id, categoryItems]);

  // localStorage에 없는 카테고리
  useEffect(() => {
    if (isNotExistLocalData) {
      if (data?.category) {
        setLevel0Category(data?.category);
        setSelectedCategoryDBId(parseInt(match.params.id));
      } else {
        showSnackbar({
          message: 'No category found',
          autoHideDuration: 5000,
          isShowBtn: true,
          btnContent: 'Home',
          btnOnClick: () => history.push(baseUrl),
        });
      }
    }
  }, [isNotExistLocalData, data, match.params.id]);

  if (loading || (!isNotExistLocalData && !level1Category) || (isNotExistLocalData && !level0Category)) {
    return isMobile ? (
      <MobileProductListPageSkeleton type="category" />
    ) : (
      <DesktopProductListPageSkeleton type="category" productCount={DESKTOP_PRODUCT_LIST_REQUEST_COUNT} />
    );
  }

  return (
    <div id={`${isMobile ? 'mobile' : 'desktop'}-category-product-list-wrapper`}>
      <SEO
        title={data?.category?.seoTitle || ''}
        titleTemplate={data?.shop?.name}
        titleSeparator="-"
        description={data?.category?.seoDescription || ''}
        image={data?.category?.backgroundImage?.url || ''}
        pathname={`${data?.shop?.domain?.url}${productsUrl}category/${data?.category?.slug || ''}/`}
        siteLanguage="en"
        siteLocale="en_GB"
        twitterUsername=""
      />

      {isMobile ? (
        <MobileCategoryProductListTemplate
          selectedCategoryDBId={selectedCategoryDBId}
          isNotExistLocalData={isNotExistLocalData}
          isLevelZero={isLevelZero}
          level0Category={level0Category}
          level1Category={level1Category}
          level2Category={level2Category}
          setLevel2Category={setLevel2Category}
          setSelectedCategoryDBId={setSelectedCategoryDBId}
        />
      ) : (
        <DesktopCategoryProductListTemplate
          selectedCategoryDBId={selectedCategoryDBId}
          isNotExistLocalData={isNotExistLocalData}
          isLevelZero={isLevelZero}
          level0Category={level0Category}
          level1Category={level1Category}
          level2Category={level2Category}
          setLevel2Category={setLevel2Category}
          setSelectedCategoryDBId={setSelectedCategoryDBId}
        />
      )}

      {isScrolled && <TopButton isMobile={isMobile} onClick={scrollTop} />}
    </div>
  );
};

export default CategoryPage;
